<template>
    <article>
        <h2>Requirements</h2>

        <p>
            The agent requires some privileges for getting access to container data, such as logs, performance counters and TCP sockets:
        </p>

        <ul>
            <li>privileged mode (<var>securityContext.privileged: true</var>)</li>
            <li>the host process ID namespace (<var>hostPID: true</var>)</li>
            <li><var>/sys/fs/cgroup</var>, <var>/sys/kernel/tracing</var>, and <var>/sys/kernel/debug</var> should be mounted to the agent's container</li>
        </ul>

        <h2 id="kubernetes">Kubernetes</h2>
        <p>
            You can install the agent using Helm:
        </p>

        <CodeSnippet>
        <pre v-hl.bash>
helm repo add coroot https://coroot.github.io/helm-charts
helm repo update
helm install --namespace coroot --create-namespace coroot-node-agent coroot/node-agent
        </pre>
        </CodeSnippet>

        <p>
            If you use <a href="https://github.com/prometheus-operator/prometheus-operator" target="_blank">Prometheus Operator</a>,
            you will also need to create a PodMonitor:
        </p>

        <pre v-hl.yaml>
apiVersion: monitoring.coreos.com/v1
kind: PodMonitor
metadata:
  name: coroot-node-agent
  namespace: coroot
spec:
  selector:
    matchLabels:
      app: coroot-node-agent
  podMetricsEndpoints:
    - port: http
        </pre>

        <p>
            Make sure the PodMonitor matches <var>podMonitorSelector</var> defined in your Prometheus:
        </p>

    <pre v-hl.yaml>
apiVersion: monitoring.coreos.com/v1
kind: Prometheus
...
spec:
  ...
  podMonitorNamespaceSelector: {}
  podMonitorSelector: {}
  ...
    </pre>

    <p>
        The special value <var>{}</var> allows Prometheus to watch all the PodMonitors from all namespaces.
    </p>

    <h2 id="docker">Docker</h2>

    <p>
        To ingest telemetry data, the agent must have the address of the Coroot instance and the capability to establish TCP connections with it.
        Please replace <var>&lt;COROOT_IP&gt;</var> with the actual IP address of your Coroot instance.
    </p>

        <CodeSnippet>
        <pre v-hl.bash>
docker run --detach --name coroot-node-agent \
  --pull=always \
  --privileged --pid host \
  -v /sys/kernel/tracing:/sys/kernel/tracing:rw \
  -v /sys/kernel/debug:/sys/kernel/debug:rw \
  -v /sys/fs/cgroup:/host/sys/fs/cgroup:ro \
  ghcr.io/coroot/coroot-node-agent \
  --cgroupfs-root=/host/sys/fs/cgroup \
  --collector-endpoint=http://&lt;COROOT_IP&gt;:8080 \
  --scrape-interval=15s
        </pre>
        </CodeSnippet>

        <p>To read the agent log:</p>

        <CodeSnippet>
        <pre v-hl.bash>
docker logs coroot-node-agent
        </pre>
        </CodeSnippet>

        <p>To uninstall the agent run the following command:</p>

        <CodeSnippet>
        <pre v-hl.bash>
docker rm -f coroot-node-agent
        </pre>
        </CodeSnippet>

        <h2 id="vm">Linux VM or bare-metal server</h2>

        <p>
            To ingest telemetry data, the agent must have the address of the Coroot instance and the capability to establish TCP connections with it.
            Please replace <var>&lt;COROOT_IP&gt;</var> with the actual IP address of your Coroot instance.
        </p>

        <p>
            This script downloads the latest version of the agent and installs it as a Systemd service. Additionally, it generates an uninstall script.
        </p>

        <CodeSnippet>
        <pre v-hl.bash>
curl -sfL https://raw.githubusercontent.com/coroot/coroot-node-agent/main/install.sh | \
  COLLECTOR_ENDPOINT=http://&lt;COROOT_IP&gt;:8080 \
  SCRAPE_INTERVAL=15s \
  sh -
        </pre>
        </CodeSnippet>

        You can read the agent log using the <var>journalctl</var> command:

        <CodeSnippet>
        <pre v-hl.bash>
sudo journalctl -u coroot-node-agent
        </pre>
        </CodeSnippet>

        <p>To uninstall the agent run the command below:</p>

        <CodeSnippet>
        <pre v-hl.bash>
/usr/bin/coroot-node-agent-uninstall.sh
        </pre>
        </CodeSnippet>


        <h2 id="flags">Flags</h2>

    <pre v-hl.bash>
usage: coroot-node-agent [&lt;flags&gt;]

Flags:
--listen="0.0.0.0:80"  Listen address - ip:port or :port
--cgroupfs-root="/sys/fs/cgroup" The mount point of the host cgroupfs root
--disable-log-parsing  Disable container logs parsing
--disable-pinger       Disable container upstreams ping
--disable-l7-tracing   Disable L7 tracing
--ephemeral-port-range="32768-60999" Destination and Listen TCP ports from this range will be skipped
--track-public-network=TRACK-PUBLIC-NETWORK ... Allow track connections to the specified IP networks, all private networks are allowed by default (e.g., Y.Y.Y.Y/mask)
--provider=PROVIDER    `provider` label for `node_cloud_info` metric
--region=REGION        `region` label for `node_cloud_info` metric
--availability-zone=AVAILABILITY-ZONE `availability_zone` label for `node_cloud_info` metric
--instance-type=INSTANCE_TYPE `instance_type` label for `node_cloud_info` metric
--instance-life-cycle=INSTANCE_LIFE_CYCLE `instance_life_cycle` label for `node_cloud_info` metric
    </pre>

    </article>
</template>

<script>

export default {
}
</script>

<style scoped>
</style>
